import { useApolloClient } from '@apollo/client';
import { DetailQuery, DetailQueryVariables } from 'graphql/query/__generated__/DetailQuery';
import { DETAIL_QUERY } from 'graphql/query/detailQuery';
import { useZoomToMarker } from 'hooks/map/useZoomToMarker';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { useUpdateEffect } from 'usehooks-ts';

// This hooks manages the initial actions on load when query parameters are set
// and listens to relevant events to update query parameters in the URL

export const useUrlGuard = (mapId: string, language?: string) => {
    const ctxView = useContext(CtxView);
    const ctxModal = useContext(CtxModal);
    const { getTokenForMapId } = useWorldMapLocalStorage();
    const navigate = useNavigate();
    const { detailId } = useParams() as { detailId: string };
    const analytics = useAnalytics();
    const apollo = useApolloClient();
    const location = useLocation();

    const { zoomToMarkers } = useZoomToMarker();
    const [firstRedirectStory, setFirstRedirectStory] = useState<string | null>(detailId);

    const isInitialized = Boolean(ctxView.mapRef?.current) && Boolean(ctxView.allMarkers);

    useEffect(() => {
        if (firstRedirectStory && isInitialized) {
            goToStory(firstRedirectStory);
            setFirstRedirectStory(null);
        }
    }, [firstRedirectStory, detailId, isInitialized]);

    const goToStory = async (storyId: string) => {
        const query = await apollo.query<DetailQuery, DetailQueryVariables>({
            query: DETAIL_QUERY,
            variables: {
                worldMapId: mapId,
                token: getTokenForMapId(mapId),
                language,
                storyId
            }
        });
        const detail = query.data.moment;
        ctxModal.setDetailModal({
            id: storyId,
            detail,
            markerId: query.data?.moment?.connectedMarkers?.[0].worldMapMarkerId
        });
        analytics.track('url_redirect', {
            action: 'detail.opened'
        });

        if (detail.connectedMarkers?.length === 1) {
            zoomToMarkers(detail.connectedMarkers);
        }
    };

    function updateEventExperienceUrl() {
        if (detailId !== ctxModal.detailModal?.id) {
            if (ctxModal.detailModal?.id) {
                const parts = location.pathname.split('/');
                const [, , eventId, part] = parts;
                const url = `/event/${eventId}/${part}/${ctxModal.detailModal.id}`;
                navigate(url);
                console.log('Set', parts, url, history);
            } else {
                console.log('Unset', history);
                navigate(-1);
                ctxModal.setDetailModal(null);
            }
        }
    }

    useUpdateEffect(() => {
        if (location.pathname.includes('/event')) {
            updateEventExperienceUrl();
            return;
        }

        const url = getUrl();

        const queryParams = location.search;
        if (detailId !== ctxModal.detailModal?.id) {
            if (ctxModal.detailModal?.id) {
                console.log('Set', history);
                navigate(`${url}/${ctxModal.detailModal.id}${queryParams}`);
            } else {
                console.log('Unset', history);
                navigate(`${url}${queryParams}`);
                ctxModal.setDetailModal(null);
            }
        }
    }, [ctxModal.detailModal?.id]);

    useEffect(() => {
        if (!ctxView.map?.worldMapId) {
            return;
        }
        console.log(
            'Change pathname detail history',
            location.pathname,
            detailId,
            ctxModal.detailModal?.id
        );
        if (!detailId) {
            ctxModal.setDetailModal(null);
        } else {
            if (detailId !== ctxModal.detailModal?.id) {
                console.log('mismatch', detailId, ctxModal.detailModal?.id);
                goToStory(detailId);
            }
        }
    }, [detailId, ctxView.map?.worldMapId]);

    function getUrl() {
        if (location.pathname.includes('/m/')) {
            const customId = location.pathname.split('/m/')[1].split('/')[0];
            return `/m/${customId}`;
        } else if (location.pathname.includes('/map/')) {
            const customId = location.pathname.split('/map/')[1].split('/')[0];
            return `/map/${customId}`;
        } else if (location.pathname.includes('/map2/')) {
            const customId = location.pathname.split('/map2/')[1].split('/')[0];
            return `/map2/${customId}`;
        } else {
            console.log('Not properly used url guard');
        }
    }
};
